import { useState, FormEvent } from 'react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Button, Link, Stack, TextField, Typography } from '@mui/material';
import { authService } from '../../shared/services/auth.service';
import { useAuth } from '../../shared/hooks/useAuth';
import { useApiError } from '../../shared/errors/useApiError';
import { useNotification } from '../../shared/notifications/NotificationProvider';
import { SplitAuthLayout, MarketingBenefits } from '../../shared/components/layout/SplitAuthLayout';
import { validators } from '../../shared/validators/auth-validators';
import { PasswordField } from '../../shared/components/ui/PasswordField';
// Google OAuth temporarily disabled until OAUTH_GOOGLE_* env vars are
// wired up on test + prod (Jenkins secret files). Re-enable this import
// and the <SocialAuthButtons /> below once the backend is configured.
// import { SocialAuthButtons } from '../../shared/components/ui/SocialAuthButtons';

export function ProRegisterPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { notify } = useNotification();
  const { setSession } = useAuth();
  const { translate } = useApiError();
  const [loading, setLoading] = useState(false);

  const nextParam = searchParams.get('next');
  const next = nextParam && nextParam.startsWith('/') ? nextParam : '/become-provider';

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
  });

  const set = (key: keyof typeof form, value: string) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const emailKey = validators.email(form.email);
    const passwordKey = validators.password(form.password);
    if (emailKey || passwordKey) {
      notify.error(t((emailKey || passwordKey) as string));
      return;
    }
    if (form.password !== form.confirmPassword) {
      notify.error(t('auth.passwordMismatch'));
      return;
    }
    setLoading(true);
    try {
      const result = await authService.register({
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim(),
        password: form.password,
      });
      setSession(result);
      notify.success(t('auth.registerSuccess'));
      navigate(`/verify-email?next=${encodeURIComponent(next)}`, { replace: true });
    } catch (err) {
      notify.error(translate(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <SplitAuthLayout
      variant="pro"
      marketing={
        <Box>
          <Typography
            variant="overline"
            sx={{
              // primary.main works in both themes (saturated copper);
              // primary.light is too washed-out on the dark pro panel.
              color: 'primary.main',
              letterSpacing: '0.18em',
              fontWeight: 700,
              display: 'block',
              mb: 1,
            }}
          >
            {t('pro.registerEyebrow')}
          </Typography>
          <Typography
            variant="h3"
            sx={{ fontWeight: 800, lineHeight: 1.15, mb: 2, color: 'inherit' }}
          >
            {t('pro.heroTitle')}
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{ color: 'inherit', opacity: 0.85, fontSize: '1.0625rem' }}
          >
            {t('pro.heroSubtitle')}
          </Typography>
          <MarketingBenefits items={[t('pro.benefit1'), t('pro.benefit2'), t('pro.benefit3')]} />
        </Box>
      }
    >
      <Typography variant="h3" sx={{ mb: 1 }}>
        {t('pro.registerTitle')}
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 4 }}>
        {t('pro.registerNextHint')}
      </Typography>

      {/* Google OAuth disabled until backend env vars are set (see import above). */}
      {/* <SocialAuthButtons variant="pro" /> */}

      <Box component="form" onSubmit={handleSubmit} noValidate>
        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label={t('auth.firstName')}
              value={form.firstName}
              onChange={(e) => set('firstName', e.target.value)}
              required
              fullWidth
            />
            <TextField
              label={t('auth.lastName')}
              value={form.lastName}
              onChange={(e) => set('lastName', e.target.value)}
              required
              fullWidth
            />
          </Stack>

          <TextField
            label={t('auth.email')}
            type="email"
            value={form.email}
            onChange={(e) => set('email', e.target.value)}
            required
            fullWidth
            autoComplete="email"
          />

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <PasswordField
              label={t('auth.password')}
              value={form.password}
              onChange={(e) => set('password', e.target.value)}
              required
              fullWidth
              autoComplete="new-password"
            />
            <PasswordField
              label={t('auth.confirmPassword')}
              value={form.confirmPassword}
              onChange={(e) => set('confirmPassword', e.target.value)}
              required
              fullWidth
              autoComplete="new-password"
              error={
                form.confirmPassword.length > 0 && form.password !== form.confirmPassword
              }
              color={
                form.confirmPassword.length > 0 && form.password === form.confirmPassword
                  ? 'success'
                  : undefined
              }
              focused={
                form.confirmPassword.length > 0 && form.password === form.confirmPassword
                  ? true
                  : undefined
              }
              helperText={
                form.confirmPassword.length > 0
                  ? form.password === form.confirmPassword
                    ? t('auth.passwordMatch')
                    : t('auth.passwordMismatch')
                  : undefined
              }
            />
          </Stack>

          <Typography variant="caption" color="text.secondary">
            {t('auth.passwordHint')}
          </Typography>

          <Button
            type="submit"
            size="large"
            disabled={loading}
            fullWidth
            sx={{
              bgcolor: '#1A1816',
              color: 'common.white',
              fontWeight: 700,
              py: 1.5,
              fontSize: '1rem',
              boxShadow: 'none',
              '&:hover': { bgcolor: '#000', boxShadow: 'none' },
              '&.Mui-disabled': {
                bgcolor: 'action.disabledBackground',
                color: 'action.disabled',
              },
            }}
          >
            {loading ? t('common.loading') : t('pro.registerCta')}
          </Button>
        </Stack>
      </Box>

      <Stack spacing={1.25} sx={{ mt: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          {t('auth.alreadyHaveAccount')}{' '}
          <Link
            component={RouterLink}
            to="/pro/login"
            sx={{ color: 'primary.main', fontWeight: 600 }}
          >
            {t('auth.login')}
          </Link>
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('pro.notAProShort')}{' '}
          <Link
            component={RouterLink}
            to="/register"
            sx={{ color: 'primary.main', fontWeight: 600 }}
          >
            {t('pro.customerSignupLink')}
          </Link>
        </Typography>
      </Stack>
    </SplitAuthLayout>
  );
}
